import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS, Coordinates, Transform } from "@dnd-kit/utilities";
import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";

import { colorFromTheme } from "../../index.server";

const Styled = styled.div<{
  x: number;
  y: number;
  transform: Transform | null;
}>`
  position: fixed;

  top: ${(props) => props.y}px;
  left: ${(props) => props.x}px;

  transform: ${({ transform }) => CSS.Translate.toString(transform)};

  display: flex;
  flex-direction: column;
  z-index: 6;
  background-color: white;

  border-radius: 0.5rem 0.5rem 0 0;
  outline: 2px solid black;
`;

const StyledDragHandle = styled.div<{
  isDragging: boolean;
}>`
  cursor: grab;

  ${({ isDragging }) =>
    isDragging
      ? css`
          background-color: ${colorFromTheme("primary")};
          outline: transparent;
        `
      : css`
          background-color: white;
        `}
`;

type DraggableProps = {
  children: React.ReactNode;
};
const Draggable = (props: DraggableProps) => {
  const [{ x, y }, setCoordinates] = useState<Coordinates>({ x: 0, y: 0 });

  const mouseSensor = useSensor(MouseSensor, {});
  const touchSensor = useSensor(TouchSensor, {});
  const keyboardSensor = useSensor(KeyboardSensor, {});
  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const resizeObserver = useRef<ResizeObserver | null>(null);
  useEffect(() => {
    // Ensure the draggable element stays on-screen during window resize
    resizeObserver.current = new ResizeObserver((entries) => {
      if (entries.length === 0 || !containerRef.current) return;

      setCoordinates(({ x, y }) =>
        keepDraggableOnScreen(containerRef.current, x, y),
      );
    });
    resizeObserver.current.observe(document.body);

    return () => {
      resizeObserver.current?.disconnect();
    };
  }, []);

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={(event) => {
        const { delta } = event;
        setCoordinates(
          keepDraggableOnScreen(containerRef.current, x + delta.x, y + delta.y),
        );
      }}
    >
      <DragItem x={x} y={y} containerRef={containerRef}>
        {props.children}
      </DragItem>
    </DndContext>
  );
};

const DragItem = (props: {
  x: number;
  y: number;
  containerRef?: React.MutableRefObject<HTMLDivElement | null>;
  children: React.ReactNode;
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: "draggable",
    });

  return (
    <Styled
      ref={(el) => {
        setNodeRef(el);
        if (props.containerRef) {
          props.containerRef.current = el;
        }
      }}
      x={props.x}
      y={props.y}
      transform={transform}
    >
      <StyledDragHandle {...attributes} {...listeners} isDragging={isDragging}>
        <DragHandle />
      </StyledDragHandle>
      {props.children}
    </Styled>
  );
};

const DragHandle = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="2.8rem"
      viewBox="0 0 200 200"
      fill="#e7e7e7"
    >
      <circle cx="40" cy="70" r="20" />
      <circle cx="100" cy="70" r="20" />
      <circle cx="160" cy="70" r="20" />
      <circle cx="40" cy="130" r="20" />
      <circle cx="100" cy="130" r="20" />
      <circle cx="160" cy="130" r="20" />
    </svg>
  );
};

const keepDraggableOnScreen = (
  el: HTMLElement | null,
  targetX: number,
  targetY: number,
): { x: number; y: number } => {
  if (!el) return { x: targetX, y: targetY };

  const rect = el.getBoundingClientRect();
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;

  let x = targetX;
  let y = targetY;

  if (rect.left < 0) {
    x = 0;
  } else if (rect.right > windowWidth) {
    x = windowWidth - rect.width;
  }

  if (rect.top < 0) {
    y = 0;
  } else if (rect.bottom > windowHeight) {
    y = windowHeight - rect.height;
  }

  return { x, y };
};

export default Draggable;
